import { inject } from "@angular/core";
import { ComponentStore } from "@ngrx/component-store";
import { tapResponse } from "@ngrx/operators";
import { pipe, switchMap, tap, withLatestFrom } from "rxjs";
import { DistributionGroup } from "src/app/models/distribution-group.model";
import { Recipient, Recipients, RecipientsGroup } from "src/app/models/recipients.model";
import { User } from "src/app/models/user.model";
import { UsersApiService } from "src/app/services/users-api.service";

export interface UsersState {
  readonly users: User[];
  readonly groups: DistributionGroup[];
  readonly choosenRecipient: Recipients;
  readonly loadingGroups: boolean;
  readonly loadingUsers: boolean;
  readonly thereIsNew: boolean;
  error?: string;
}

export class UsersStore extends ComponentStore<UsersState> {
  #useraApiService = inject(UsersApiService);

  constructor() {
    super({
      users: [], groups: [],
      choosenRecipient: {
        groups: [],
        users: []
      },
      loadingGroups: false,
      loadingUsers: false,
      thereIsNew: false
    });
    this.fetchUsers();
    this.fetchGroups();
  }
  readonly loadingUsers$ = this.select(({ loadingUsers }) => loadingUsers);
  readonly loadingGroup$ = this.select(({ loadingGroups }) => loadingGroups);
  readonly users$ = this.select(({ users }) => users);
  readonly usersWithoutChoosen$ = this.select((state) =>
    state.users.filter(user => !(state.choosenRecipient.users?.find(recipient => recipient.user.id == user.id)))
  );

  readonly groupsWithoutChoosen$ = this.select((state) =>
    state.groups.filter(group => !(state.choosenRecipient.groups?.find(g => g.id == group.id)))
  );

  readonly usersfromRecipients$ = this.select(({ choosenRecipient }) => choosenRecipient.users?.map(u => u.user));
  readonly recipient$ = this.select(({ choosenRecipient }) => choosenRecipient.users);
  readonly groups$ = this.select(({ choosenRecipient }) => choosenRecipient.groups);
  readonly isRecipients$ = this.select(({ choosenRecipient }) => (choosenRecipient?.users?.length ?? 0) || (choosenRecipient?.groups?.length ?? 0) > 0);
  readonly choosenRecipient$ = this.select(({ choosenRecipient }) => choosenRecipient);

  readonly addUser = this.updater((state, user: User) => ({
    ...state,
    users: [...state.users, user]
  }));
  // readonly addRecipient = this.updater((state, user: User) => ({
  //   ...state,
  //   choosenRecipient: [...(state.choosenRecipient ?? []), user]
  // }));

  readonly addRecipient = this.updater(
    (state, user: User) => ({
      ...state,
      choosenRecipient: { ...state.choosenRecipient, users: [...state.choosenRecipient.users ?? [], { id: 0, user: user }] }
    })
  )
  readonly updateRecipients = this.updater(
    (state, recipient: Recipients) => ({
      ...state,
      choosenRecipient: recipient
    })
  )

  readonly addGroup = this.updater(
    (state, group: DistributionGroup) => ({
      ...state,
      choosenRecipient: { ...state.choosenRecipient, groups: [...state.choosenRecipient.groups ?? [], { ...group, users: [...group.users?.map(x => { return { id: 0, user: x } as Recipient })] }] }
    })
  )
  // readonly loadExistRecipients = this.updater((state, recipients: User[]) => ({
  //   ...state,
  //   choosenRecipient: recipients,
  //   users: state.users.filter(user => recipients.find(x => this.isSameUser(x, user)) == undefined)
  // }));
  readonly RemoveUser = this.updater((state, user: User) => ({
    ...state,
    users: [...state.users.filter(u => u.id != user.id)]
  }));

  readonly RemoveRecipient = this.updater((state, user: User) => ({
    ...state,
    choosenRecipient: { ...state.choosenRecipient, users: state.choosenRecipient.users?.filter(u => u.user.id !== user.id) }
  }));

  readonly RemoveGroup = this.updater((state, group: DistributionGroup) => ({
    ...state,
    choosenRecipient: { ...state.choosenRecipient, groups: state.choosenRecipient.groups?.filter(g => g.id !== group.id) }
  }));

  readonly RemoveAllNewRecipientsAndGroups = this.updater((state) => ({
    ...state,
    choosenRecipient: {
      users: state.choosenRecipient.users?.filter(r => r.id != 0),
      groups: state.choosenRecipient.groups?.filter(r => r.users?.every(u => u.id != 0))
    }

  }));
  readonly RemoveAllRecipientsAndGroups = this.updater((state) => ({
    ...state,
    choosenRecipient: { users: [], groups: [] }
  }));
  // ,.filter(u => !this.isSameUser(u, user))

  readonly fetchUsers = this.effect<void>(
    pipe(
      tap(() => this.patchState({ loadingUsers: true })),
      switchMap(() =>
        this.#useraApiService.getUsers().pipe(
          withLatestFrom(this.recipient$),
          tapResponse(
            ([u, recipients]) => {
              this.patchState({
                users: u
                // recipients ? u.filter(user => recipients.find(x => this.isSameUser(x, user)) == undefined) : u
                , loadingUsers: false
              })
            },
            (error: Error) =>
              this.patchState({ error: error?.message, loadingUsers: false })
          )
        )
      )
    )
  );
  readonly fetchGroups = this.effect<void>(
    pipe(
      tap(() => this.patchState({ loadingGroups: true })),
      switchMap(() =>
        this.#useraApiService.getGroups().pipe(
          withLatestFrom(this.recipient$),
          tapResponse(
            ([groups, recipients]) => {
              this.patchState({
                groups: groups.result
                // recipients ? u.filter(user => recipients.find(x => this.isSameUser(x, user)) == undefined) : u
                , loadingGroups: false
              })
            },
            (error: Error) =>
              this.patchState({ error: error?.message, loadingGroups: false })
          )
        )
      )
    )
  );
}